import { useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import images from "../../images";
import {
  useGetUnreadInboxCountQuery,
  useGetUnreadQuestHistoryCountQuery,
  useGetUserInfoQuery,
  useGetStickerListQuery,
  useLogoutMutation,
} from "../../services/apiSlice";

import posthog from "posthog-js";
import "react-image-crop/dist/ReactCrop.css";
import Dialog from "../../components/common/Dialog";
import ProfileChevron from "./ProfileCommon/ProfileChevron";
import ProfileCard from "./ProfileCommon/RallyCoinsCard";
import Button from "../../components/v6/Button";

function Profile() {
  const navigate = useNavigate();
  const [logOut, { isLoading: isLoadLogout }] = useLogoutMutation();
  const { data: userInfo, isLoading: isLoadingProfile } = useGetUserInfoQuery({
    refetchOnMountOrArgChange: true,
  });
  const { data: unreadData } = useGetUnreadInboxCountQuery();
  const { data: unreadQuestHistoryData } = useGetUnreadQuestHistoryCountQuery();
  const { data: stickerListData } = useGetStickerListQuery();

  const { t } = useTranslation();
  const logoutModalRef = useRef();

  const unreadCount = unreadData?.total || 0;
  const unreadQuestHistoryCount = unreadQuestHistoryData?.total || 0;
  const stickerList = useMemo(() => {
    const list = stickerListData?.data || [];
    const topThree = list.slice(0, 3);
    if (topThree.length >= 3) return topThree;

    const placeholders = Array.from(
      { length: 3 - topThree.length },
      (_, index) => ({
        id: `sticker-empty-${index}`,
        background: "transparent",
      })
    );

    return [...topThree, ...placeholders];
  }, [stickerListData]);

  const genderMapping = useMemo(() => {
    return {
      male: t("signUp.gender.male"),
      female: t("signUp.gender.female"),
      maleIdentifying: t("signUp.gender.maleIdentifying"),
      femaleIdentifying: t("signUp.gender.femaleIdentifying"),
      nonBinary: t("signUp.gender.nonBinary"),
      agender: t("signUp.gender.agender"),
      preferNotToSay: t("signUp.gender.preferNotToSay"),
    };
  }, [t]);

  const handleLogOut = async () => {
    try {
      const allKeys = Object.keys(localStorage);
      const timerKeys = allKeys.filter(
        (key) => key.startsWith("timer_") || key.startsWith("initialTime_")
      );

      timerKeys.forEach((key) => localStorage.removeItem(key));

      const response = await logOut();
      if (response.data.message === "Operation successful") {
        localStorage.clear();
        sessionStorage.clear();
        posthog.reset();
        window.location.href = "/sign-in";
      }
    } catch (err) {
      console.error("Error:", err);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/sign-in";
    }
  };
  const handleEditUser = () => {
    navigate(`/profile/edit`);
  };

  const handleWithdraw = () => {
    navigate(`/profile/withdraw`);
  };

  const handleAlbum = () => {
    navigate(`/stickers`);
  };

  const handleInbox = () => {
    navigate(`/inbox`);
  };
  const handleQuestHistory = () => {
    navigate(`/profile/quest/history`);
  };

  const handleSupport = () => {
    navigate(`/profile/support`);
  };

  const handleNotification = () => {
    navigate(`/profile/notifications`);
  };
  const handleGoBack = () => {
    navigate(`/quest`);
  };
  return (
    <div className="section-content flex flex-col mx-auto bg-white w-full max-w-[480px] pb-[30px]">
      <Helmet>
        <meta name="theme-color" content="#FFFFFF" />
      </Helmet>
      <LoadingCommon statusLoading={isLoadingProfile || isLoadLogout} />
      <div className="flex flex-col w-full min-h-[912px]">
        <div className="flex flex-col flex-1 w-full gap-[14px] xs:gap-[24px] pb-[60px]">
          <div className="flex flex-col gap-3">
            <div className="w-[42px] h-[42px] rounded-full">
              <div
                onClick={handleGoBack}
                className="w-full h-full flex items-center justify-center"
              >
                <span
                  className={`icon icon-arrow-left text-[#A0A0A0] text-[18px]`}
                />
              </div>
            </div>
            <div className="flex flex-col gap-[14px] xs:gap-[18px]">
              <div className="flex flex-row items-center gap-[8px] xs:gap-2.5">
                <h1 className="text-[34px] xs:text-4xl font-bold text-[#253FF6]">
                  {userInfo?.data?.username
                    ? userInfo?.data?.username
                    : t("profile.username")}
                </h1>
                <img
                  onClick={handleEditUser}
                  className="w-6 cursor-pointer"
                  src={images.EditIconBlue}
                  alt=""
                />
              </div>
              <div>
                <span className="text-[16px] xs:text-lg font-medium text-[#253FF6]">
                  {userInfo?.data?.age},{" "}
                  {genderMapping[userInfo?.data?.gender] ||
                    userInfo?.data?.gender}
                  , Singapore (SGD)
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-[10px] xs:gap-[12px] mt-4">
            {/* 1. Rally Coins Card */}
            <ProfileCard
              coinAmount={userInfo?.data?.token ?? "0"}
              dollarValue={
                userInfo?.data?.token ? userInfo?.data?.token / 100 : "0"
              }
              onWithdraw={handleWithdraw}
              subTitle={t("profile.rallyCoins")}
              iconName="rally-coin"
              className="min-h-[140px] xs:min-h-[170px]"
            />

            {/* 2. Stickers Card */}
            <ProfileCard
              className="mt-4 bg-[#E1D2F380] min-h-[120px] xs:min-h-[150px]"
              subTitle={t("profile.stickers")}
              stickers={stickerList}
              onAlbum={handleAlbum}
              iconName="sticker"
              itemClassName="!size-[48px] xs:!size-[50px] 2xs:!size-[50px]"
              contentContainerClassName="!gap-0"
            />

            {/* 3. XP Card */}
            <ProfileCard
              className="bg-[#f4f2f2] !pr-2"
              subTitle={t("profile.rallyXP")}
              currentXp={userInfo?.data?.xp ?? "0"}
              total={userInfo?.data?.level_object.max_xp || 0}
              level={userInfo?.data?.level_object.no}
              iconName="xp"
            />
          </div>

          <div>
            <ProfileChevron
              title={t("profile.inbox")}
              isShowNotificationBadge={true}
              onClick={handleInbox}
              unreadCount={unreadCount}
            />
            <ProfileChevron
              title={t("profile.questHistory")}
              isShowNotificationBadge={true}
              onClick={handleQuestHistory}
              unreadCount={unreadQuestHistoryCount}
            />
            <ProfileChevron
              title={t("profile.notificationsSettings")}
              onClick={handleNotification}
            />
            <ProfileChevron
              title={t("profile.support")}
              onClick={handleSupport}
            />
          </div>

          <div className="w-full flex justify-center">
            <Button
              handleClick={() => logoutModalRef.current.open()}
              className="gap-2.5 self-stretch px-5 mt-2 xs:mt-5 w-full text-[18px] xs:text-xl font-medium tracking-tight text-center text-primary bg-white rounded-md border border-primary h-[50px]"
            >
              {t("profile.logout")}
            </Button>
          </div>
        </div>
      </div>

      <Dialog ref={logoutModalRef} hiddenCloseButton>
        <div className="flex flex-col gap-6">
          <p className="text-center">{t("profile.logoutConfirmation")}</p>
          <div className="flex gap-2">
            <Button
              className="text-[16px] xs:text-lg rounded-xl h-[50px] bg-white border border-mid-gray text-mid-gray flex-1"
              handleClick={() => logoutModalRef.current.close()}
            >
              {t("base.cancel")}
            </Button>
            <Button
              className="text-[16px] xs:text-lg rounded-xl h-[50px] bg-primary border border-primary text-white flex-1"
              handleClick={handleLogOut}
            >
              {t("base.yes")}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default Profile;
